'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { toolWorkspaceSelectionRequired } from '@/components/marketing/tools/tool-workspace-picker'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  buildToolWorkspaceTargetPath,
  selectedToolWorkspaceFromSearch,
} from '@/lib/tool-workspace-selection'

export function useToolWorkspaceOwner({
  pagePath,
  lockedWorkspaceId,
}: {
  pagePath: string
  lockedWorkspaceId?: string
}) {
  const { org, organizations, switchOrganization } = useAuthContext()
  const [requestedWorkspaceId, setRequestedWorkspaceId] = useState<
    string | null
  >(null)
  const [manualSwitching, setManualSwitching] = useState(false)
  const [workspaceSwitchError, setWorkspaceSwitchError] = useState<
    string | null
  >(null)
  const autoSwitchStartedRef = useRef<string | null>(null)
  const search = useSyncExternalStore(
    () => () => {},
    () => window.location.search,
    () => '',
  )
  const availableIds = useMemo(
    () => organizations.data.map(workspace => workspace.id),
    [organizations.data],
  )
  const selectedFromUrl = selectedToolWorkspaceFromSearch(search, availableIds)
  // The active workspace is the default owner for a run: it is the one the
  // visitor is already signed into, so no switch happens and the picker still
  // lets them pick another. Only an account whose active org is not in its
  // own list (a stale session) has to choose explicitly.
  const activeWorkspaceId =
    org?.id && availableIds.includes(org.id) ? org.id : null
  const selectedWorkspaceId =
    requestedWorkspaceId ??
    selectedFromUrl ??
    activeWorkspaceId ??
    (organizations.count === 1 ? (organizations.data[0]?.id ?? null) : null)

  const activateWorkspace = useCallback(
    (workspaceId: string) => {
      setRequestedWorkspaceId(workspaceId)
      setManualSwitching(true)
      setWorkspaceSwitchError(null)
      void switchOrganization({
        organizationId: workspaceId,
        targetPath: buildToolWorkspaceTargetPath(pagePath, workspaceId),
      }).catch(() => {
        setManualSwitching(false)
        setWorkspaceSwitchError('Could not switch workspace. Please try again.')
      })
    },
    [pagePath, switchOrganization],
  )

  useEffect(() => {
    if (lockedWorkspaceId || !organizations.isLoaded) return
    if (!selectedWorkspaceId || org?.id === selectedWorkspaceId) return
    if (autoSwitchStartedRef.current === selectedWorkspaceId) return
    autoSwitchStartedRef.current = selectedWorkspaceId
    void switchOrganization({
      organizationId: selectedWorkspaceId,
      targetPath: buildToolWorkspaceTargetPath(pagePath, selectedWorkspaceId),
    }).catch(() => {
      autoSwitchStartedRef.current = null
      setWorkspaceSwitchError('Could not switch workspace. Please try again.')
    })
  }, [
    lockedWorkspaceId,
    org?.id,
    organizations,
    pagePath,
    selectedWorkspaceId,
    switchOrganization,
  ])

  const workspaceSwitching =
    manualSwitching ||
    Boolean(selectedWorkspaceId && org?.id !== selectedWorkspaceId)

  const workspaceSelectionRequired = toolWorkspaceSelectionRequired({
    workspaceCount: organizations.count,
    selectedWorkspaceId,
    lockedWorkspaceId,
  })
  const billingWorkspaceId = lockedWorkspaceId ?? selectedWorkspaceId
  const upgradeReturn = useMemo(
    () =>
      billingWorkspaceId
        ? {
            successUrl: buildToolWorkspaceTargetPath(
              pagePath,
              billingWorkspaceId,
              { upgrade: 'success' },
            ),
            cancelUrl: buildToolWorkspaceTargetPath(
              pagePath,
              billingWorkspaceId,
              { upgrade: 'cancel' },
            ),
          }
        : {
            successUrl: `${pagePath}?upgrade=success`,
            cancelUrl: `${pagePath}?upgrade=cancel`,
          },
    [billingWorkspaceId, pagePath],
  )
  // A credit pack checkout comes back with no `upgrade` outcome: the API
  // appends `credit_topup=pending&purchase_id=…` to the success address, and
  // the page confirms that purchase instead of announcing a plan upgrade.
  const topUpReturn = useMemo(
    () =>
      billingWorkspaceId
        ? {
            successUrl: buildToolWorkspaceTargetPath(
              pagePath,
              billingWorkspaceId,
            ),
            cancelUrl: buildToolWorkspaceTargetPath(
              pagePath,
              billingWorkspaceId,
              { credit_topup: 'cancel' },
            ),
          }
        : {
            successUrl: pagePath,
            cancelUrl: `${pagePath}?credit_topup=cancel`,
          },
    [billingWorkspaceId, pagePath],
  )

  return {
    activateWorkspace,
    organizations,
    selectedWorkspaceId,
    topUpReturn,
    upgradeReturn,
    workspaceSelectionRequired,
    workspaceSwitchError,
    workspaceSwitching,
  }
}
